import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { clearLineLogin, postLineAuth, readLineInvite, readLineCheckin, validateLineState } from '@/auth/line';
import { AuthBlocked, type AuthBlock } from './AuthBlocked';

/**
 * LINE OAuth redirect target (`/auth/line/callback`). Reads the code + state,
 * verifies via the Edge Function, then drops back to /welcome — which routes the
 * new session home (or to profile setup for a brand-new account).
 */
export function LineCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [blocked, setBlocked] = useState<AuthBlock | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // OAuth code is single-use — never run twice
    ran.current = true;
    const code = params.get('code');
    const state = params.get('state');
    if (params.get('error') || !code || !validateLineState(state)) {
      clearLineLogin();
      setBlocked('error');
      return;
    }
    const invite = readLineInvite(state);
    const checkin = readLineCheckin(state);
    clearLineLogin(); // consumed — don't let it linger (localStorage isn't auto-purged)
    void (async () => {
      const res = await postLineAuth(code, invite);
      if (!res.ok) { setBlocked(res.reason); return; }
      signIn();
      // A scanned venue QR routes straight to attendance check-in; else home via /welcome.
      navigate(checkin ? `/learn/course/${checkin.course}/attendance?checkin=${checkin.token}` : '/welcome', { replace: true });
    })();
  }, [params, navigate, signIn]);

  if (blocked) {
    return (
      <div style={{ minHeight: '100dvh', background: 'var(--paper)', display: 'grid', placeItems: 'center', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <AuthBlocked kind={blocked} onRetry={() => navigate('/welcome', { replace: true })} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--paper)', display: 'grid', placeItems: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, color: 'var(--ink-3)' }}>
        <div className="wp-line-spin" style={{ width: 36, height: 36, borderRadius: 99, border: '3px solid var(--line-2)', borderTopColor: '#06C755' }} />
        <div style={{ fontSize: 14.5 }}>Signing you in with LINE…</div>
      </div>
      <style>{`.wp-line-spin{animation:wplinespin .8s linear infinite}@keyframes wplinespin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
